import { ArrowRight, CalendarDays, ChevronRight, CircleDot, Crown, ListOrdered, ShieldCheck, Sparkles, Trophy, Users } from 'lucide-react';
import { formations, playerById } from '../data';
import { PlayerAvatar } from '../components/PlayerAvatar';
import { ClubBadge } from '../components/ClubBadge';
import type { GameState } from '../gameState';
import { TeamBadge } from '../components/TeamBadge';
import { createLeagueGroups, createStandings, findClubGroup, teamSeasonCode } from '../competition';
import { clubCalendarFixtures } from '../careerCalendar';
import { AFFINITY_USER_TEAM_ID } from '../specialCompetitions';
import { AffinityCrest } from '../components/AffinityCrest';
import { AffinityGamesGlobe } from '../components/AffinityGamesGlobe';

export function DashboardPage({ game, onNavigate }: { game: GameState; onNavigate: (page: string) => void }) {
  const totalTokens = Object.values(game.save.tokens).reduce((sum, value) => sum + value, 0);
  const groups = createLeagueGroups(game.save.leagueSeed, game.save.clubName);
  const clubGroup = findClubGroup(groups);
  const upcoming = clubCalendarFixtures(game.save).filter((fixture) => !fixture.result).slice(0, 3).map((fixture) => {
    const userId = fixture.competition === 'affinity_games' ? AFFINITY_USER_TEAM_ID : 'team_aurora';
    return { ...fixture, userId, opponent: fixture.homeId === userId ? fixture.away! : fixture.home! };
  });
  const nextMatch = upcoming[0];
  const standings = createStandings(clubGroup, game.save.leagueResults, game.save.leaguePointAdjustments);
  const compactStandings = standings.slice(0, 5);
  const opponent = nextMatch?.opponent;
  const affinityUserElement = nextMatch?.competition === 'affinity_games' ? game.save.affinityGames?.element : undefined;
  const affinityOpponentElement = nextMatch?.competition === 'affinity_games' ? opponent?.players[0]?.element : undefined;
  const activeFormation = [...formations, ...game.save.customFormations].find((formation) => formation.id === game.save.formationId) ?? formations[0];
  const ownedById = new Map(game.ownedPlayers.map((player) => [player.id, player]));

  return (
    <div className="page dashboard-page">
      <header className="page-heading">
        <div>
          <span className="eyebrow">CENTRO DEL MÁNAGER</span>
          <h1>Buenos días, míster.</h1>
          <p>Todo listo para el siguiente paso de {game.save.clubName}.</p>
        </div>
        <div className="season-chip"><CalendarDays size={16} /> Temporada {game.save.season} · {clubGroup.name} · Jornada {nextMatch?.matchday ?? '—'}</div>
      </header>

      <section className="dashboard-grid">
        <article className={`next-match-card ${nextMatch?.competition === 'champions' ? 'next-match-card--champions' : ''} ${nextMatch?.competition === 'kings_cup' ? 'next-match-card--kings-cup' : ''} ${nextMatch?.competition === 'affinity_games' ? 'next-match-card--affinity-games' : ''}`}>
          <div className="match-card-topline">
            <span><CircleDot size={14} /> PRÓXIMO PARTIDO</span>
            <span>{nextMatch ? competitionHeading(nextMatch.competition, nextMatch.label, clubGroup.name) : `Liga · ${clubGroup.name}`}</span>
          </div>
          <div className="versus-block">
            <div className="club-side">
              {affinityUserElement ? <AffinityCrest element={affinityUserElement} size="hero" /> : <ClubBadge save={game.save} size="lg" />}
              <div className="club-identity">
                <strong>{nextMatch?.competition === 'affinity_games' ? nextMatch.homeId === nextMatch.userId ? nextMatch.home?.name ?? 'Selección temporal' : nextMatch.away?.name ?? 'Selección temporal' : game.save.clubName}</strong>
                <small>{nextMatch?.neutral ? 'NEUTRAL' : nextMatch?.homeId === nextMatch?.userId ? 'LOCAL' : 'VISITANTE'}</small>
              </div>
            </div>
            <div className="versus-center">
              <span>{nextMatch ? competitionChip(nextMatch.competition, nextMatch.matchday) : 'JORNADA —'}</span>
              <strong>18:00</strong>
              <em>VS</em>
            </div>
            <div className="club-side">
              {affinityOpponentElement ? <AffinityCrest element={affinityOpponentElement} size="hero" /> : opponent && <TeamBadge name={opponent.name} imageUrl={opponent.image_url} size="lg" />}
              <div className="club-identity">
                <strong>{opponent?.name ?? 'Por determinar'}</strong>
                <small>{nextMatch?.neutral ? 'NEUTRAL' : nextMatch?.homeId === nextMatch?.userId ? 'VISITANTE' : 'LOCAL'}</small>
              </div>
            </div>
          </div>
          <div className="match-card-footer">
            <div className="readiness"><ShieldCheck size={17} /><span><b>Equipo preparado</b><small>11 titulares · {activeFormation.name}</small></span></div>
            <button className="primary-action" onClick={() => onNavigate('match')}>Jugar partido <ArrowRight size={17} /></button>
          </div>
        </article>

        <article className="focus-card lineup-preview-card card">
          <div className="lineup-preview-head">
            <div className="card-label"><Users size={15} /> ONCE PREVISTO</div>
            <span>{activeFormation.name}</span>
          </div>
          <div className="focus-lineup-pitch" aria-label={`Once previsto: ${activeFormation.name}`}>
            <i className="focus-pitch-outline" />
            <i className="focus-pitch-half" />
            <i className="focus-pitch-circle" />
            {activeFormation.positions.map(([x, y], index) => {
              const player = ownedById.get(game.save.lineup[index]);
              if (!player) return null;
              return <span
                className={`focus-lineup-player element-ring--${player.element.toLowerCase()}`}
                style={{ left: `${x + 10}%`, top: `${8 + y * 0.84}%` }}
                title={`${player.name} · ${activeFormation.roles[index]}`}
                key={`${player.id}-${index}`}
              >
                <PlayerAvatar player={player} size="sm" />
                <b>{index + 1}</b>
                {game.save.captainPlayerId === player.id && <em className="focus-captain">C</em>}
              </span>;
            })}
          </div>
          <button className="text-action lineup-preview-action" onClick={() => onNavigate('tactics')}>Gestionar alineación <ChevronRight size={16} /></button>
        </article>
      </section>

      <section className="metric-row">
        <article className="metric-card"><span className="metric-icon metric-blue"><ShieldCheck size={18} /></span><div><small>PLANTILLA</small><strong>{game.ownedPlayers.length}</strong><span>jugadores disponibles</span></div></article>
        <article className="metric-card"><span className="metric-icon metric-orange"><Sparkles size={18} /></span><div><small>RECURSOS</small><strong>{totalTokens}</strong><span>fichas de reclutamiento</span></div></article>
        <article className="metric-card"><span className="metric-icon metric-green"><Trophy size={18} /></span><div><small>ÚLTIMO PARTIDO</small><strong>{game.save.lastResult ? `${game.save.lastResult.homeScore} — ${game.save.lastResult.awayScore}` : '—'}</strong><span>{game.save.lastResult ? `Jornada ${game.save.leagueMatchday}` : 'Sin partidos disputados'}</span></div></article>
        <article className="metric-card"><span className="metric-icon metric-purple"><CircleDot size={18} /></span><div><small>REPUTACIÓN</small><strong>62</strong><span>proyecto emergente</span></div></article>
      </section>

      <section className="card upcoming-card">
        <div className="section-title"><div><span className="eyebrow">CALENDARIO</span><h2>Próximos partidos del club</h2></div><button className="ghost-action" onClick={() => onNavigate('calendar')}>Ver calendario</button></div>
        <div className="upcoming-list">
          {upcoming.map((item, index) => {
            const isChampions = item.competition === 'champions';
            const isSpecial = item.competition !== 'league';
            const stage = isSpecial ? item.label.replace(/^(Champions|Affinity Games|King's Cup)\s*·\s*/i, '') : item.label;
            const opponentAffinity = item.competition === 'affinity_games' ? item.opponent.players[0]?.element : undefined;
            return <article className={`${index === 0 ? 'featured' : ''} upcoming-${item.competition}`} key={item.id}>
              {isSpecial ? (
                <span className={`upcoming-competition-logo upcoming-competition-logo--${item.competition}`} aria-label={competitionName(item.competition)} title={competitionName(item.competition)}>
                  {item.competition === 'champions' ? <Trophy size={23} strokeWidth={2.4} aria-hidden="true" /> : item.competition === 'kings_cup' ? <Crown size={23} strokeWidth={2.4} aria-hidden="true" /> : <AffinityGamesGlobe />}
                  <i aria-hidden="true" />
                </span>
              ) : (
                <span className="upcoming-round"><small>JORNADA</small><b>{item.matchday}</b></span>
              )}
              {opponentAffinity ? <AffinityCrest element={opponentAffinity} /> : <TeamBadge name={item.opponent.name} imageUrl={item.opponent.image_url} size="sm" />}
              <span className="upcoming-opponent"><b>{item.opponent.name}</b><small>{isSpecial ? <><em>{competitionName(item.competition).toUpperCase()}</em>{stage}</> : <>{stage} · {item.homeId === item.userId ? 'En casa' : 'Fuera'}</>}</small></span>
              <span className="upcoming-time">{item.date.slice(5).split('-').reverse().join('/')}<small>{index === 0 ? 'PRÓXIMO' : 'PENDIENTE'}</small></span>
            </article>;
          })}
        </div>
      </section>

      <section className="dashboard-bottom-grid">
        <article className="card activity-card compact-activity">
          <div className="section-title"><div><span className="eyebrow">CLUB</span><h2>Últimos fichajes</h2></div></div>
          <div className="timeline">
            {game.save.recruits.length ? game.save.recruits.slice(0, 5).map((recruit) => {
              const player = playerById.get(recruit.playerId);
              return player && <div className="timeline-item" key={`${recruit.playerId}-${recruit.recruitedAt}`}><span className="timeline-mark" /><div><b>{player.name} se une al equipo</b><small>Reclutamiento · {player.position} · {player.element}</small></div></div>;
            }) : <div className="empty-state compact"><span className="empty-orbit" /><b>Tu historia empieza aquí</b><p>Los fichajes y resultados aparecerán en este espacio.</p></div>}
          </div>
        </article>

        <article className="card dashboard-table-card">
          <div className="section-title"><div><span className="eyebrow">{clubGroup.name.toUpperCase()}</span><h2>Clasificación de liga</h2></div><button className="ghost-action" onClick={() => onNavigate('competitions')}><ListOrdered size={14} /> Completa</button></div>
          <div className="mini-standings-head"><span>#</span><span>EQUIPO</span><span>PJ</span><span>DG</span><span>PTS</span></div>
          <div className="mini-standings">
            {compactStandings.map((row) => <div className={row.team.id === 'team_aurora' ? 'is-club' : ''} key={row.team.id}><b>{row.position}</b><span>{row.team.id === 'team_aurora' ? <ClubBadge save={game.save} size="sm" /> : <TeamBadge name={row.team.name} imageUrl={row.team.image_url} size="sm" />}<strong>{row.team.name}<em>{teamSeasonCode(row.team)}</em></strong></span><small>{row.played}</small><small>{row.goalDifference > 0 ? `+${row.goalDifference}` : row.goalDifference}</small><b>{row.points}</b></div>)}
          </div>
        </article>
      </section>
    </div>
  );
}

function competitionName(competition: 'league' | 'champions' | 'kings_cup' | 'affinity_games') {
  return ({ league: 'Liga regular', champions: 'Inazuma Champions', kings_cup: "King's Cup", affinity_games: 'Affinity Games' })[competition];
}

function competitionHeading(competition: 'league' | 'champions' | 'kings_cup' | 'affinity_games', label: string, group: string) {
  if (competition === 'league') return `Liga · ${group}`;
  return `${competitionName(competition)} · ${label.split(' · ').at(-1)}`;
}

function competitionChip(competition: 'league' | 'champions' | 'kings_cup' | 'affinity_games', matchday?: number) {
  if (competition === 'champions') return 'CHAMPIONS';
  if (competition === 'kings_cup') return "KING'S CUP";
  if (competition === 'affinity_games') return 'AFFINITY GAMES';
  return `JORNADA ${matchday ?? '—'}`;
}
